import {
  type ClassificationLevel,
  type EgressAction,
  egressPolicySchema,
  privateLevel,
} from "@esi/shared";

/**
 * What each choice of a classification level does, and its impact in practice (openspec change
 * classification-editor, design D3), as i18n keys and values under `adminSettings.levels`, never
 * strings: the editor words them, the tests pin the branches. Computed from the rules the server
 * enforces: the readers floor, the download right, the egress gate's policy for the level's
 * egress floor, declassification, and the default and private levels.
 */

export type Floor = ClassificationLevel["readersFloor"];

/** One sentence: a key under `adminSettings.levels` and its interpolation values. */
export interface Sentence {
  key: string;
  values?: Record<string, string | number>;
}

/** The tenant's settings the wording reads (effective at the tenant level). */
export interface ImpactContext {
  /** `tools.egress`: the gate's action per readers floor; null when unknown. */
  egress: Record<Floor, EgressAction> | null;
  /** `knowledge.declassification`. */
  declassification: boolean;
  /** `knowledge.defaultClassification`: a level id. */
  defaultClassification: string | null;
  /** The private level's id (`privateLevel` of the draft scheme), if any. */
  privateLevelId: string | null;
}

/** Reads the context from the effective settings, as the catalogue lists them. */
export function impactContext(
  scheme: readonly ClassificationLevel[],
  settings: readonly { key: string; value: unknown }[],
): ImpactContext {
  const value = (key: string) => settings.find((s) => s.key === key)?.value;
  const egress = egressPolicySchema.safeParse(value("tools.egress"));
  const declassification = value("knowledge.declassification");
  const defaultClassification = value("knowledge.defaultClassification");
  return {
    egress: egress.success ? egress.data : null,
    // Its code default: on.
    declassification: declassification !== false,
    defaultClassification: typeof defaultClassification === "string" ? defaultClassification : null,
    privateLevelId: privateLevel(scheme)?.id ?? null,
  };
}

/** The floor outbound tools treat the level as: its `egressFloor`, else its readers floor. */
export const egressOf = (level: Pick<ClassificationLevel, "readersFloor" | "egressFloor">) =>
  level.egressFloor ?? level.readersFloor;

export type Choice = "name" | "readers" | "download" | "outbound";

type LevelChoices = Pick<
  ClassificationLevel,
  "id" | "readersFloor" | "downloadAllowed" | "egressFloor"
>;

/** The sentence under one choice, for its current value. */
export function choiceConsequence(
  choice: Choice,
  level: LevelChoices,
  context: ImpactContext,
): Sentence {
  switch (choice) {
    case "name":
      return { key: "consequence.name" };
    case "readers":
      return { key: `consequence.readers.${level.readersFloor}` };
    case "download":
      return {
        key: level.downloadAllowed ? "consequence.download.on" : "consequence.download.off",
      };
    case "outbound": {
      const floor = egressOf(level);
      const action = context.egress?.[floor];
      const treated = level.egressFloor !== undefined && level.egressFloor !== level.readersFloor;
      if (!action) {
        return {
          key: treated
            ? `consequence.outbound.treatedUnknown.${floor}`
            : "consequence.outbound.unknown",
        };
      }
      return {
        key: treated
          ? `consequence.outbound.treated.${floor}.${action}`
          : `consequence.outbound.readers.${action}`,
      };
    }
  }
}

/**
 * The "In practice" summary of a level: who reads, download, outbound tools, declassification,
 * then its special roles (default for new files, files of conversations outside projects).
 */
export function levelImpact(level: LevelChoices, context: ImpactContext): Sentence[] {
  const sentences: Sentence[] = [
    choiceConsequence("readers", level, context),
    choiceConsequence("download", level, context),
    choiceConsequence("outbound", level, context),
    {
      key: context.declassification ? "impact.declassification.on" : "impact.declassification.off",
    },
  ];
  if (level.id === context.defaultClassification) {
    sentences.push({ key: "impact.default" });
  }
  if (level.id === context.privateLevelId) {
    sentences.push({ key: "impact.conversationFiles" });
  }
  return sentences;
}

/** The icon family of an impact sentence (D2: users, download, globe, shield, file, message). */
export function impactIcon(
  sentence: Sentence,
):
  | "readers"
  | "download"
  | "noDownload"
  | "outbound"
  | "declassification"
  | "default"
  | "conversation" {
  const { key } = sentence;
  if (key.startsWith("consequence.readers")) {
    return "readers";
  }
  if (key === "consequence.download.on") {
    return "download";
  }
  if (key === "consequence.download.off") {
    return "noDownload";
  }
  if (key.startsWith("consequence.outbound")) {
    return "outbound";
  }
  if (key.startsWith("impact.declassification")) {
    return "declassification";
  }
  return key === "impact.default" ? "default" : "conversation";
}

/**
 * The collapsed row's summary (D2): readers, download, outbound, then its roles; keys under
 * `adminSettings.levels.summary`.
 */
export function levelSummary(level: LevelChoices, context: ImpactContext): Sentence[] {
  const parts: Sentence[] = [
    { key: `summary.readers.${level.readersFloor}` },
    { key: level.downloadAllowed ? "summary.download" : "summary.noDownload" },
    {
      key:
        level.egressFloor === undefined || level.egressFloor === level.readersFloor
          ? "summary.outbound.readers"
          : `summary.outbound.${level.egressFloor}`,
    },
  ];
  if (level.id === context.defaultClassification) {
    parts.push({ key: "summary.default" });
  }
  if (level.id === context.privateLevelId) {
    parts.push({ key: "summary.conversationFiles" });
  }
  return parts;
}
